import ContinueButton from '@app-studio-web/ui/components/ContinueButton'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useLocation, useNavigate, useSubmit } from 'react-router-dom'

export const QUESTION_STEPS = [
  'location',
  'ai-reaction',
  'career-priority',
  'field',
  'role',
  'experience',
  'level',
  'proud-skill',
  'company-size',
  'industry',
  'ai-impact',
  'daily-tasks',
  'repetitiveness',
  'ai-tools',
  'ai-frequency',
  'ai-witness',
  'concern-feedback',
  'data-frequency',
  'writing-frequency',
  'decision-making',
  'job-security',
  'education',
  'salary-range',
  'summary',
  'final-word',
]

export type AcriAnswer = {
  title: string
  subtitle?: string
  value: string
  emoji?: string
}

export default function OnboardingQuestionPage({
  eyebrow,
  title,
  subtitle,
  answers,
  multiple = false,
  layout = 'list',
}: {
  eyebrow?: string
  title: string
  subtitle?: string
  answers: AcriAnswer[]
  multiple?: boolean
  layout?: 'list' | 'grid'
}) {
  const submit = useSubmit()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const [selectedRadio, setSelectedRadio] = useState<string | null>(null)

  const currentPath = location.pathname.split('/').pop() || ''
  const currentIndex = QUESTION_STEPS.indexOf(currentPath)
  const stepNumber = currentIndex + 1
  const totalSteps = QUESTION_STEPS.length
  const progress = totalSteps > 0 ? (stepNumber / totalSteps) * 100 : 0

  const defaultEyebrow =
    stepNumber === totalSteps ? 'LAST ONE' : `STEP ${stepNumber}`

  const questionFieldName = t(title, { lng: 'en' })

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    setSelectedRadio(event.target.value)
    submit(event.currentTarget.form)
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target
    setSelectedValues((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value),
    )
  }

  const isGrid = layout === 'grid'

  return (
    <div className="bg-app-bg text-primary flex min-h-dvh flex-col font-sans">
      <div className="bg-accent/30 fixed top-0 left-0 z-10 h-full w-1">
        <div
          className="bg-accent w-full transition-all duration-500"
          style={{ height: `${progress}%` }}
        />
      </div>

      <div className="bg-border h-1 w-full">
        <div
          className="bg-accent h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="text-muted hover:text-primary flex cursor-pointer items-center gap-1 bg-transparent text-sm transition-colors"
        >
          ← {t('shared.back', { defaultValue: 'Back' })}
        </button>
        <span className="text-muted text-sm">
          {stepNumber} / {totalSteps}
        </span>
      </div>

      <div
        className={`mx-auto w-full max-w-2xl grow px-6 pt-10 ${multiple ? 'pb-28' : 'pb-16'}`}
      >
        <span className="text-accent text-xs font-semibold tracking-widest uppercase">
          {eyebrow ?? defaultEyebrow}
        </span>

        <h1 className="mt-3 font-serif text-[1.75rem] leading-tight font-bold tracking-tight md:text-[2.25rem]">
          {t(title)}
        </h1>

        {subtitle && (
          <p className="text-muted mt-4 text-base leading-relaxed">
            {t(subtitle)}
          </p>
        )}

        <Form method="post" className="mt-12">
          <div className={isGrid ? 'grid grid-cols-3 gap-3' : 'space-y-4'}>
            {answers.map((answer) => {
              const isChecked = selectedValues.includes(answer.value)
              const isRadioSelected = selectedRadio === answer.value
              return (
                <label
                  key={answer.value}
                  className={
                    isGrid
                      ? `flex cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 py-4 text-center font-semibold transition-colors ${
                          isRadioSelected
                            ? 'border-accent bg-accent text-white'
                            : 'border-border text-primary hover:border-accent/40 bg-white'
                        }`
                      : `hover:border-accent/40 flex w-full cursor-pointer items-center gap-3 rounded-xl border bg-white px-5 py-4 transition-colors ${
                          multiple && isChecked
                            ? 'border-accent/50'
                            : 'border-border'
                        }`
                  }
                >
                  <input
                    className="hidden"
                    name={questionFieldName}
                    onChange={
                      multiple ? handleCheckboxChange : handleRadioChange
                    }
                    type={multiple ? 'checkbox' : 'radio'}
                    value={answer.value}
                  />
                  {!isGrid && multiple && (
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                        isChecked
                          ? 'border-accent bg-accent text-white'
                          : 'border-[#ccc] bg-white'
                      }`}
                    >
                      {isChecked && (
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                  )}
                  {answer.emoji && (
                    <span className="shrink-0 text-xl">{answer.emoji}</span>
                  )}
                  {isGrid ? (
                    <span className="truncate text-sm">{t(answer.title)}</span>
                  ) : (
                    <div className="flex flex-col">
                      <span className="text-primary font-semibold">
                        {t(answer.title)}
                      </span>
                      {answer.subtitle && (
                        <span className="text-muted mt-0.5 text-sm">
                          {t(answer.subtitle)}
                        </span>
                      )}
                    </div>
                  )}
                </label>
              )
            })}
          </div>

          {multiple && (
            <div
              className={`border-border bg-app-bg fixed bottom-0 left-0 z-20 w-full border-t px-6 py-4 transition-all duration-200 ${
                selectedValues.length === 0
                  ? 'translate-y-full opacity-0'
                  : 'translate-y-0 opacity-100'
              }`}
            >
              <div className="mx-auto max-w-2xl">
                <ContinueButton disabled={selectedValues.length === 0} />
              </div>
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}
